import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useLocation} from 'react-router-dom';

import featureBatch from 'assets/v3/image-translator/feature-batch.webp';
import featureLayout from 'assets/v3/image-translator/feature-layout.webp';
import featureLink from 'assets/v3/image-translator/feature-link.webp';
import featureRefine from 'assets/v3/image-translator/feature-refine.webp';
import heroImg from 'assets/v3/image-translator/hero.webp';

import {JsonLd, buildProductJsonLd, PageCta, PageHero, PageShell, PageTitle, Section} from 'components';
import {ROUTES} from 'constants/routes';
import Icon from 'components/v3/Icon';
import {btnPrimaryLg, btnSecondary} from 'components/v3/classes';

const DOCUMENT_PATHS = [
  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
];
const COG_PATHS = [
  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
];
const LINK_PATHS = [
  'M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244',
];
const GRID_PATHS = [
  'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
];

interface Feature {
  descKey: string;
  iconPaths: string[];
  image: string;
  /** CTA 跳站内路由（如链接采集能力 → 插件落地页）；缺省跳翻译控制台外链 */
  internalTo?: string;
  /** 文右图左（false=图左文右） */
  reversed?: boolean;
  titleKey: string;
}

interface UseCase {
  descKey: string;
  titleKey: string;
}

const renderUseCase = (useCase: UseCase, t: (key: string) => string) => (
  <div className="glass-card rounded-2xl p-6" key={useCase.titleKey}>
    <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 mb-5" />
    <h3 className="text-base font-semibold text-slate-900 mb-2">{t(useCase.titleKey)}</h3>
    <p className="text-sm text-slate-500 leading-relaxed">{t(useCase.descKey)}</p>
  </div>
);

const ImageTranslator: FC = () => {
  const {t} = useTranslation('products');
  const {search} = useLocation();

  // 与首页一致：透传 utm_source；链接指向新 workspace（2026-09-16，原 /console/workspace）
  const utmSource = new URLSearchParams(search).get('utm_source');
  const translateNowUrl = `https://www.xiangjifanyi.com/workspace/image-translator${
    utmSource ? `?utm_source=${utmSource}` : ''
  }`;

  // 核心卖点：一图一卖点，图文左右交替（2026-09-17 重排，原「大图+四小卡」布局废弃）
  const features: Feature[] = [
    {
      descKey: 'image.features.layout.desc',
      iconPaths: DOCUMENT_PATHS,
      image: featureLayout,
      titleKey: 'image.features.layout.title',
    },
    {
      descKey: 'image.features.refine.desc',
      iconPaths: COG_PATHS,
      image: featureRefine,
      reversed: true,
      titleKey: 'image.features.refine.title',
    },
    {
      descKey: 'image.features.batch.desc',
      iconPaths: GRID_PATHS,
      image: featureBatch,
      titleKey: 'image.features.batch.title',
    },
    {
      descKey: 'image.features.linkCapture.desc',
      iconPaths: LINK_PATHS,
      image: featureLink,
      internalTo: ROUTES.extension,
      reversed: true,
      titleKey: 'image.features.linkCapture.title',
    },
  ];

  const useCases: UseCase[] = [
    {descKey: 'image.useCases.crossBorderListing.desc', titleKey: 'image.useCases.crossBorderListing.title'},
    {descKey: 'image.useCases.socialAd.desc', titleKey: 'image.useCases.socialAd.title'},
    {descKey: 'image.useCases.independentSite.desc', titleKey: 'image.useCases.independentSite.title'},
    {descKey: 'image.useCases.multiPlatform.desc', titleKey: 'image.useCases.multiPlatform.title'},
  ];

  return (
    <PageShell>
      <PageTitle
        canonicalUrl="https://www.xiangjifanyi.com/home/image-translator"
        description={t('image.descLine1')}
        title={t('image.title')}
      />
      <JsonLd
        data={buildProductJsonLd({
          homeLabel: t('common:nav.homeAria'),
          path: '/image-translator',
          productDescription: t('image.descLine1'),
          productName: t('image.title'),
        })}
      />
      <PageHero
        actions={
          <a className={btnPrimaryLg} href={translateNowUrl} rel="noreferrer" target="_blank">
            {t('image.cta')}
          </a>
        }
        aside={
          <img
            alt={t('image.title')}
            className="w-full max-w-xl mx-auto rounded-3xl shadow-2xl"
            decoding="async"
            src={heroImg}
          />
        }
        badge={t('image.hero.badge')}
        description={
          <>
            {t('image.descLine1')}
            <br />
            {t('image.descLine2')}
          </>
        }
        title={t('image.title')}
        variant="light"
      />
      <Section title={t('image.capabilitiesTitle')}>
        <div className="space-y-16 lg:space-y-24">
          {features.map((feature) => (
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" key={feature.titleKey}>
              <img
                alt={t(feature.titleKey)}
                className={`w-full rounded-2xl shadow-xl ${feature.reversed ? 'lg:order-2' : ''}`}
                decoding="async"
                loading="lazy"
                src={feature.image}
              />
              <div className={feature.reversed ? 'lg:order-1' : ''}>
                <div className="w-12 h-12 rounded-lg bg-indigo-50 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-indigo-600" paths={feature.iconPaths} />
                </div>
                <h3 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight mb-4">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-slate-500 lg:text-lg leading-relaxed mb-7">{t(feature.descKey)}</p>
                {feature.internalTo ? (
                  <Link className={btnSecondary} to={feature.internalTo}>
                    {t('image.cta')}
                  </Link>
                ) : (
                  <a className={btnSecondary} href={translateNowUrl} rel="noreferrer" target="_blank">
                    {t('image.cta')}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section bg="muted" title={t('image.useCasesTitle')}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{useCases.map((u) => renderUseCase(u, t))}</div>
      </Section>
      <PageCta
        actions={
          <a className={btnPrimaryLg} href={translateNowUrl} rel="noreferrer" target="_blank">
            {t('image.cta')}
          </a>
        }
        description={t('image.descLine2')}
        title={t('image.ctaTitle')}
      />
    </PageShell>
  );
};

export default ImageTranslator;
